import { ToastContainer } from "react-toastify";
import Header from "../../components/Layout/Header";
import SideBar from "../../components/Layout/SideBar";
import Footer from "../../components/Layout/Footer";
import { useEffect } from "react";
import { useProfil } from "../../stores/Layout/profil";
import { getUserInformation } from "../../services/Api/loginService";
import {
  linkGoogle,
  linkOffice,
  refreshSession,
  updateProfil,
} from "../../services/Api/userService";
import { showToastError, showToastSuccess } from "../../services/Toasts/toasts";
import useModal from "../../hooks/useModal";
import DeleteProfil from "../../components/Layout/DeleteProfil";
import { useTokenContext } from "../../context/tokenContext";
import { useMsal } from "@azure/msal-react";

const Profil = () => {
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    phone,
    setPhone,
    title,
    setTitle,
    lastPassword,
    setLastPassword,
    newPassword,
    setNewPassword,
    update,
    setUpdate,
    reset,
  } = useProfil();
  const { accountType, setAccountType} = useTokenContext();
  // Hook
  const { modalContent, isModalOpen, openModal, closeModal } = useModal();

  useEffect(() => {
    getUserInformation()
      .then((res) => {
        setFirstName(res?.data?.firstname);
        setLastName(res?.data?.lastname);
        setEmail(res?.data?.eMail);
        setPhone(res?.data?.tel);
        setTitle(res?.data?.titre);
        setAccountType(res?.data?.accountType);
      })
      .catch(() => showToastError("Une erreur s'est produite"));

  }, [update]);

  const handleUpdateProfil = () => {
    const data = {
      lastName,
      firstName,
      email,
      lastPassword,
      newPassword,
      titre: title,
      tel: phone,
    };

    updateProfil(data)
      .then(() => {
        showToastSuccess("Votre profil à bien été modifié");
        setNewPassword("");
        setLastPassword("");
        setUpdate(!update);
        reset();
      })
      .catch(() => showToastError("Une erreur s'est produite"));

  };

  // Login with google
  const loginWithGoogle = (credentialResponse: any) => {
    linkGoogle(credentialResponse.credential)
      .then(() => {
        refreshSession()
          .then(() => {
            showToastSuccess("Votre compte à bien été lié");
            setUpdate(!update);
          })
          .catch((error) => {
            console.error(error);
          });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const loginErrror = () => {
    showToastError("Une erreur est survenue lors de la connexion.");
  };

  const { instance } = useMsal();

  // Login with office
  const handleLoginOffice = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    instance
      .loginPopup({
        scopes: ["user.read"],
      })
      .then((response) => {
        linkOffice(response.idToken, response.accessToken)
          .then(() => {
            refreshSession()
              .then(() => {
                showToastSuccess("Votre compte à bien été lié");
                setUpdate(!update);
              })
              .catch((error) => {
                console.error(error);
              });
          })
          .catch(() => showToastError("Une erreur s'est produite"));
      })
      .catch(() => showToastError("Une erreur s'est produite"));
  };

  return (
    <div className="w-screen h-full bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col justify-start items-start">
      <div className="w-full fixed top-0 left-0 right-0 z-50 h-14">
          <Header />
      </div>
      <div className="w-full h-full flex justify-start items-start mb-20">
        <SideBar />
        <ToastContainer position="top-center" />
        {isModalOpen ? modalContent : null}
        
        {/* Main Content */}
        <div className="flex flex-col justify-start items-center w-11/12 h-full m-auto ml-20 mt-10">
          {/* Header Section */}
          <div className="w-full h-full max-w-6xl mx-auto px-6 py-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-light-lavender to-dark-lavender rounded-full mb-6 shadow-2xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-nxm-dark-gray mb-2 font-K2D">
                Mon Profil
              </h1>
              <p className="text-lg text-grey-trio max-w-2xl mx-auto">
                Gérez vos informations personnelles et sécurisez votre compte
              </p>
            </div>

            {/* Profile Form */}
            <div className="bg-white rounded-3xl shadow-3xl p-8 mb-8 backdrop-blur-sm border border-white/20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-nxm-dark-gray mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-3 text-light-lavender" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Informations Personnelles
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-grey-trio mb-2">Prénom</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-light-lavender focus:ring-4 focus:ring-light-lavender/20 transition-all duration-300 form-input-modern"
                        placeholder="Votre prénom"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-grey-trio mb-2">Nom</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-light-lavender focus:ring-4 focus:ring-light-lavender/20 transition-all duration-300 form-input-modern"
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-nxm-dark-gray mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-3 text-light-lavender" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Contact
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-grey-trio mb-2">Email</label>
                      <input
                        type="email"
                        disabled={accountType === "google" || accountType === "office"}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-300 form-input-modern ${
                          accountType === "google" || accountType === "office"
                            ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                            : "border-gray-200 bg-white focus:border-light-lavender focus:ring-light-lavender/20"
                        }`}
                        placeholder="votre@email.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-grey-trio mb-2">Téléphone</label>
                      <input
                        type="tel"
                        disabled={accountType === "google" || accountType === "office"}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-300 form-input-modern ${
                          accountType === "google" || accountType === "office"
                            ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                            : "border-gray-200 bg-white focus:border-light-lavender focus:ring-light-lavender/20"
                        }`}
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Title Section */}
              <div className="mt-8 pt-8 border-t border-gray-100">
                <h3 className="text-xl font-semibold text-nxm-dark-gray mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-3 text-light-lavender" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Poste
                </h3>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full max-w-md px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-light-lavender focus:ring-4 focus:ring-light-lavender/20 transition-all duration-300 form-input-modern"
                  placeholder="Votre poste ou titre"
                />
              </div>
            </div>

            {/* Password Section - Only for local accounts */}
            {accountType === "local" && (
              <div className="bg-white rounded-3xl shadow-3xl p-8 mb-8 backdrop-blur-sm border border-white/20">
                <h3 className="text-xl font-semibold text-nxm-dark-gray mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-3 text-light-lavender" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Sécurité du Mot de Passe
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-grey-trio mb-2">Ancien mot de passe</label>
                    <input
                      type="password"
                      value={lastPassword}
                      onChange={(e) => setLastPassword(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-light-lavender focus:ring-4 focus:ring-light-lavender/20 transition-all duration-300 form-input-modern"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-grey-trio mb-2">Nouveau mot de passe</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-light-lavender focus:ring-4 focus:ring-light-lavender/20 transition-all duration-300 form-input-modern"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Account Linking Section - Only for local accounts */}
            {/* {accountType === "local" && (
              <div className="bg-white rounded-3xl shadow-3xl p-8 mb-8 backdrop-blur-sm border border-white/20">
                <h3 className="text-xl font-semibold text-nxm-dark-gray mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-3 text-light-lavender" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-.758l1.102-1.101a4 4 0 00-5.656-5.656l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                  </svg>
                    Lier votre Compte
                  </h3>
                  <p className="text-grey-trio mb-6">Connectez votre compte à d'autres services pour une connexion plus rapide</p>
                  <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    className="w-1/2 cursor-pointer h-12 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-xl shadow-sm hover:shadow-md hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 flex items-center justify-center space-x-3 group"
                    onClick={() => {
                      // Déclencher le processus de connexion Google
                      // Vous devrez implémenter cette logique ou utiliser GoogleLogin en arrière-plan
                      const googleLoginElement = document.querySelector('[data-testid="google-login"]');
                      if (googleLoginElement) {
                        (googleLoginElement as HTMLElement).click();
                      }
                    }}
                  >
                    <img
                      src={google}
                      alt="logo Google"
                      className="h-5 w-5 transition-transform duration-300 group-hover:scale-110"
                    />
                    <span>Se connecter avec Google</span>
                  </button>

                  <div className="hidden">
                    <GoogleLogin 
                      onSuccess={loginWithGoogle} 
                      onError={loginErrror}
                      data-testid="google-login"
                    />
                  </div>

                  <button
                    className="w-1/2 h-12 cursor-pointer bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-xl shadow-sm hover:shadow-md hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 flex items-center justify-center space-x-3 group"
                    onClick={(e) => handleLoginOffice(e)}
                  >
                    <img
                      src={microsoft}
                      alt="logo microsoft"
                      className="h-5 w-5 transition-transform duration-300 group-hover:scale-110"
                    />
                    <span>Se connecter avec Office</span>
                  </button>
                </div>
              </div>
            )} */}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-3xl shadow-3xl p-8 backdrop-blur-sm border border-white/20">
              <button
                className="flex items-center px-6 py-3 text-red-500 bg-red-50 border-2 border-red-200 font-medium rounded-xl hover:bg-red-100 hover:border-red-300 transition-all duration-300 btn-modern"
                onClick={() => openModal(<DeleteProfil closeModal={closeModal} />)}
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Supprimer le compte
              </button>
              
              <button
                className="flex items-center px-8 py-3 bg-gradient-to-r from-light-lavender to-dark-lavender text-white font-medium rounded-xl hover:from-dark-lavender hover:to-light-lavender transition-all duration-300 btn-modern shadow-lg"
                onClick={handleUpdateProfil}
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Sauvegarder les modifications
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profil;
